/**
 * Share Challenges Card
 * Share on social platforms to earn XP
 */

import { useState } from 'react';
import { Share2, Check, Zap, Loader2, Twitter, Facebook, Linkedin, MessageCircle, Send, Globe } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { SharePlatformId, ShareChallenge } from '../types';
import { openShareWindow } from '../utils/helpers';
import { SHARE_PLATFORMS, MAX_SHARES_PER_DAY } from '../utils/constants';

interface ShareChallengesCardProps {
  sharesToday: number;
  shareXpRewards: ShareChallenge[];
  canShare: boolean;
  onShare: (platform: SharePlatformId) => Promise<void>;
}

// Map icon names to components
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Twitter,
  Facebook,
  Linkedin,
  MessageCircle,
  Send,
  Globe,
};

function XpBadge({ xp, completed }: { xp: number; completed: boolean }) {
  return (
    <div
      className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
        completed
          ? 'bg-green-100 text-green-700'
          : 'bg-gray-100 text-gray-600'
      }`}
    >
      {completed ? (
        <Check className="w-4 h-4" />
      ) : (
        <Zap className="w-4 h-4" />
      )}
      <span>+{xp} XP</span>
    </div>
  );
}

export function ShareChallengesCard({
  sharesToday,
  shareXpRewards,
  canShare,
  onShare,
}: ShareChallengesCardProps) {
  const { t } = useLanguage();
  const [isSharing, setIsSharing] = useState<SharePlatformId | null>(null);

  const handleShare = async (platform: SharePlatformId) => {
    if (!canShare || isSharing) return;

    setIsSharing(platform);
    try {
      // Open share window
      openShareWindow(platform);
      // Record the share
      await onShare(platform);
    } catch (error) {
      console.error('Failed to share:', error);
    } finally {
      setIsSharing(null);
    }
  };

  const allSharesComplete = sharesToday >= MAX_SHARES_PER_DAY;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Share2 className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('challenges.share.title', 'Share Challenges')}
            </h2>
            <p className="text-sm text-gray-500">
              {t('challenges.share.subtitle', 'Share Rumio and earn up to 550 XP daily!')}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* XP Breakdown */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            {t('challenges.share.xpBreakdown', 'XP Breakdown')}
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {shareXpRewards.map((reward, index) => (
              <div key={index} className="text-center">
                <div className="text-xs text-gray-500 mb-1">
                  {t(`challenges.share.share${reward.shareNumber}`, `${reward.shareNumber}${['st', 'nd', 'rd'][index]} Share`)}
                </div>
                <XpBadge xp={reward.xpReward} completed={reward.completed} />
              </div>
            ))}
          </div>
        </div>

        {/* Platform Buttons */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            {t('challenges.share.platforms', 'Share on')}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {SHARE_PLATFORMS.map((platform) => {
              const Icon = iconMap[platform.icon] || Share2;
              const isLoading = isSharing === platform.id;
              const isDisabled = !canShare || isSharing !== null;

              return (
                <button
                  key={platform.id}
                  onClick={() => handleShare(platform.id)}
                  disabled={isDisabled}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
                    allSharesComplete
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : isDisabled
                      ? 'opacity-50 cursor-wait'
                      : `${platform.bgColor} ${platform.color} ${platform.hoverBgColor} transform hover:scale-105`
                  }`}
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : allSharesComplete ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                  <span>{platform.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Status message */}
        {allSharesComplete && (
          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-sm text-green-700 font-medium">
              {t('challenges.share.complete', "You've completed all share challenges for today!")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
